import {
  CompanyRoleIdWithName,
  DecisionContext,
  toCompanyRoleIdWithName,
} from "../common/commonDomain";
import { makeAutoObservable } from "mobx";
import { CompanyRole } from "../../types/CompanyInfo";
import { identity } from "lodash";
import { sortedBoard } from "../../utils/Roles";
import { DecisionType } from "../../common/decisions";
import { BooleanFieldModel, OptionFieldModel, StringFieldModel } from "../../common/commonForms";
import { TMessage } from "../../i18n/tt";

export enum SignatureAssignment {
  chairpersonAlone = "chairpersonAlone",
  chairpersonAndMember = "chairpersonAndMember",
  twoMembers = "twoMembers",
  everyMember = "everyMember",
  ceoAlone = "ceoAlone",
  namedPersons = "namedPersons",
  other = "other",
}

export type BoardAssignsSignature = {
  type: DecisionType.boardAssignsSignature;
  signatureAssignedTo: Array<SignatureAssignment>;
  signatureAssignedToNamedBoardMembers: Array<CompanyRoleIdWithName>;
  signatureAssignedToNamedPersons: string;
  signatureAssignedOtherRegulation: string;
  revokeBoardDecisionsSignatures: boolean;
};

export class BoardAssignsSignatureForm {
  readonly type = DecisionType.boardAssignsSignature;
  signatureAssignedTo: OptionFieldModel<SignatureAssignment>;
  signatureAssignedToNamedBoardMembers: OptionFieldModel<CompanyRoleIdWithName>;
  signatureAssignedToNamedPersons: StringFieldModel;
  signatureAssignedOtherRegulation: StringFieldModel;
  revokeBoardDecisionsSignatures: BooleanFieldModel;

  constructor(
    private context: DecisionContext,
    public data?: BoardAssignsSignature,
  ) {
    this.signatureAssignedTo = new OptionFieldModel({
      value: data?.signatureAssignedTo,
      options: Object.values(SignatureAssignment),
      idFn: identity,
      labelFn: (x) => this.decisionsSignatureAssignedTo(x),
    });
    this.signatureAssignedToNamedPersons = new StringFieldModel({
      value: data?.signatureAssignedToNamedPersons || "",
    });
    this.signatureAssignedToNamedBoardMembers = new OptionFieldModel({
      value: data?.signatureAssignedToNamedBoardMembers,
      options: this.boardMembers.map(toCompanyRoleIdWithName),
      idFn: (r) => r.companyroleId.toString(),
      labelFn: (r) => ({ translated: r.name }),
    });
    this.signatureAssignedOtherRegulation = new StringFieldModel({
      value: data?.signatureAssignedOtherRegulation || "",
    });
    this.revokeBoardDecisionsSignatures = new BooleanFieldModel({
      value: data?.revokeBoardDecisionsSignatures,
    });
    makeAutoObservable(this);
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid() {
    return this.signatureAssignedTo.selected.length > 0;
  }

  get isShowNamedPersonInput() {
    return this.signatureAssignedTo?.isSelected(SignatureAssignment.namedPersons);
  }

  get isShowOtherRegulationsInput() {
    return this.signatureAssignedTo?.isSelected(SignatureAssignment.other);
  }

  get boardMembers(): CompanyRole[] {
    return sortedBoard(this.context.companyInfo.roles);
  }

  decisionsSignatureAssignedTo(x: SignatureAssignment): TMessage {
    return {
      key: `decisions.signature.assignedTo.${x}`,
    };
  }

  validate(): boolean {
    return this.isValid;
  }
}
